import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { applyResolvedThemeWithTransition, type ApplyThemeOptions } from '../theme/themeTransition';
import {
  getSystemDark,
  resolveTheme,
  type ResolvedTheme,
  type ThemePreference,
} from '../theme/resolveTheme';
import { readThemePreference, writeThemePreference } from '../theme/storage';
import {
  applyHubSpotChatColorScheme,
  registerHubSpotChatColorSchemeFix,
} from '../lib/hubspot/hubspotChatColorScheme';

interface ThemeContextValue {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference, options?: ApplyThemeOptions) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    typeof window === 'undefined' ? 'light' : readThemePreference()
  );
  const [systemDark, setSystemDark] = useState(getSystemDark);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const desired = resolveTheme(preference, systemDark);

  /**
   * `resolved` trails `desired` until the new tokens are on <html>. Consumers that read
   * computed CSS (D3 charts) would otherwise redraw during the parent effect's commit,
   * while the outgoing theme is still applied.
   */
  const [resolved, setResolved] = useState<ResolvedTheme>(desired);
  const pendingApplyOptionsRef = useRef<ApplyThemeOptions | undefined>(undefined);
  const appliedOnceRef = useRef(false);

  useEffect(() => {
    if (appliedOnceRef.current && desired === resolved) {
      return;
    }
    appliedOnceRef.current = true;

    const options = pendingApplyOptionsRef.current;
    pendingApplyOptionsRef.current = undefined;

    applyResolvedThemeWithTransition(desired, {
      ...options,
      onApplied: ({ insideTransition }) => {
        if (insideTransition) {
          // Redraw before the new snapshot is captured so the reveal animates over
          // finished pixels instead of competing with D3 for the main thread.
          flushSync(() => setResolved(desired));
          return;
        }
        setResolved(desired);
      },
    });
  }, [desired, resolved]);

  useEffect(() => {
    registerHubSpotChatColorSchemeFix(() => resolved);
    applyHubSpotChatColorScheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference, options?: ApplyThemeOptions) => {
    writeThemePreference(next);
    pendingApplyOptionsRef.current = options;
    setPreferenceState(next);
  }, []);

  const value = useMemo(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
};
